import { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

/**
 * One page of a list, as the server sends it.
 *
 * `total` arrives on the first page only — it is a second query, and it cannot change under a
 * filter the caller is not changing. `nextCursor` is null at the end of the list.
 */
export interface Page<T> {
  items: T[]
  nextCursor: string | null
  total: number | null
}

/** Query-string values a list screen filters by. Undefined and empty are both left off. */
export type ListParams = Record<string, string | number | boolean | undefined | null>

export function listQuery(params: ListParams, cursor: string | null): string {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value))
    }
  }

  if (cursor) {
    search.set('cursor', cursor)
  }

  const query = search.toString()
  return query ? `?${query}` : ''
}

/**
 * A list that loads a page at a time.
 *
 * Every list screen used to fetch a bare `Take(50)` or `Take(200)` and show whatever came back, so
 * a shop ringing up two hundred sales a day could not reach this morning's by lunchtime and nothing
 * on screen said a row was missing. This is the client half of the fix: pages are appended as they
 * are asked for, and the caller gets the total so the screen can say how much it is showing.
 *
 * The filters go in `params` and are part of the query key, so changing a date or a search term
 * starts a fresh list rather than appending to the previous question's answer.
 */
/**
 * Rows a page holds.
 *
 * Twenty, matching the tables that page in the browser, so every list in the app turns over at the
 * same rate. Fifty was the first choice and ran past the bottom of the screen, which left whatever
 * sits under the list out of sight — the thing paging was supposed to fix.
 */
export const LIST_PAGE = 20

export function usePagedList<T>(
  key: unknown[],
  path: string,
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const query = useInfiniteQuery({
    queryKey: [...key, params],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiClient.get<Page<T>>(`${path}${listQuery({ ...params, pageSize: LIST_PAGE }, pageParam)}`),
    getNextPageParam: (last: Page<T>) => last.nextCursor,
    // A screen that renders a "you cannot see this" panel must not fetch anyway: the request would
    // come back 403 and, on a list behind a permission, that is a console full of errors on every
    // load for the users who legitimately do not have it.
    enabled: options.enabled ?? true,
  })

  const [index, setIndex] = useState(0)
  const filters = JSON.stringify(params)

  // Changing a date or a search term asks a different question, and the answer starts at its first
  // page. Without this the screen would open on page four of a list that may now be one page long.
  useEffect(() => setIndex(0), [filters])

  const pages = query.data?.pages ?? []
  const current = Math.min(index, Math.max(0, pages.length - 1))

  return {
    ...query,
    /** The rows of the page being looked at — what the screen renders. */
    page: (pages[current]?.items ?? []) as T[],
    /** Every row fetched so far, flattened. For a screen that totals what it has. */
    items: pages.flatMap((entry) => entry.items) as T[],
    /** How many rows match the filter in total, or null before the first page lands. */
    total: pages[0]?.total ?? null,
    pageIndex: current,
    /** The row numbers on screen, 1-based, for "21–40 of 214". */
    from: current * LIST_PAGE + 1,
    hasPrevious: current > 0,
    /** A page already fetched, or one the server says is still to come. */
    hasNext: current + 1 < pages.length || query.hasNextPage,

    previous: () => setIndex((entry) => Math.max(0, entry - 1)),

    next: async () => {
      // Only fetched when it has not been already: paging back and forth must not re-ask the
      // server for pages it has given once.
      if (current + 1 >= pages.length && query.hasNextPage) {
        await query.fetchNextPage()
      }

      setIndex((entry) => entry + 1)
    },
  }
}

export type PagedList<T> = ReturnType<typeof usePagedList<T>>


/**
 * Draws the first slice of a list that already arrived whole.
 *
 * Not every long list is a paged endpoint. A report comes back in one piece — it has to, because
 * the totals under it are computed from all of it — and a year of daily rows or a catalogue's worth
 * of dead stock is then several thousand table cells drawn at once, which locks a phone up at the
 * counter for seconds. The rows are already in memory, so this governs only how many are painted.
 *
 * Deliberately not virtualised scrolling: a report is read, sorted and exported rather than
 * scrolled through, and a button says plainly how much is being held back where a scrollbar does
 * not.
 */
export function useVisibleRows<T>(rows: readonly T[], step = 100) {
  const [shown, setShown] = useState(step)

  // A fresh result starts at the top rather than keeping the last one depth.
  useEffect(() => setShown(step), [rows, step])

  return {
    visible: rows.slice(0, shown) as T[],
    hidden: Math.max(0, rows.length - shown),
    total: rows.length,
    shown: Math.min(shown, rows.length),
    more: () => setShown((current) => current + step),
  }
}
